// ═══════════════════════════════════════════════════════════════
// Centrální typy DocEditoru
// ═══════════════════════════════════════════════════════════════

/** Odkud pochází aktuálně otevřený dokument. */
export type DocSource = 'local' | 'gdrive' | 'unsaved';

/** Nastavení stránky (karta Rozložení). */
export interface PageSetup {
  size: 'A4' | 'Letter' | 'Legal';
  orientation: 'portrait' | 'landscape';
  /** Okraje v milimetrech. */
  margins: { top: number; right: number; bottom: number; left: number };
}

/** Přednastavené profily okrajů (mm), analogicky k Wordu. */
export const MARGIN_PRESETS: Record<string, PageSetup['margins']> = {
  normal: { top: 25.4, right: 25.4, bottom: 25.4, left: 25.4 },
  narrow: { top: 12.7, right: 12.7, bottom: 12.7, left: 12.7 },
  moderate: { top: 25.4, right: 19.05, bottom: 25.4, left: 19.05 },
  wide: { top: 25.4, right: 50.8, bottom: 25.4, left: 50.8 },
};

/** Rozměry stránky (mm) podle velikosti. */
export const PAGE_DIMENSIONS: Record<PageSetup['size'], { w: number; h: number }> = {
  A4: { w: 210, h: 297 },
  Letter: { w: 215.9, h: 279.4 },
  Legal: { w: 215.9, h: 355.6 },
};

/** Nastavení čísla stránky (karta Vložení → Číslo stránky). */
export interface PageNumberSetup {
  /** Zda je číslo stránky aktivní. */
  enabled: boolean;
  /** Umístění: v záhlaví nebo v zápatí. */
  place: 'header' | 'footer';
  /** Vodorovné zarovnání. */
  align: 'left' | 'center' | 'right';
}

export const DEFAULT_PAGE_NUMBER: PageNumberSetup = {
  enabled: false,
  place: 'footer',
  align: 'center',
};

/** Jeden komentář (nebo odpověď ve vlákně) — Fáze 3. */
export interface DocComment {
  /** Stabilní ID komentáře (číslo, mapuje se na w:id v DOCX). */
  id: number;
  /** Text komentáře (prostý text; Word podporuje i formátovaný, my zatím ne). */
  text: string;
  author: string;
  /** Iniciály autora (Word je zobrazuje v bublině). */
  initials: string;
  /** ISO timestamp vzniku. */
  date: string;
  /** ID nadřazeného komentáře — vyplněné u odpovědí ve vlákně. */
  parentId?: number;
  /** Označený jako vyřešený (Word: w15:done). */
  resolved?: boolean;
}

/** Metadata otevřeného dokumentu (ne obsah — ten drží TipTap). */
export interface DocMeta {
  /** Zobrazovaný název (název souboru bez přípony). */
  name: string;
  source: DocSource;
  /** Handle na lokální soubor (File System Access). Neserializuje se do JSON. */
  fileHandle?: FileSystemFileHandle;
  /** ID souboru na Google Disku (Fáze 2). */
  gdriveId?: string;
  /** Zda má dokument neuložené změny. */
  dirty: boolean;
  /** Timestamp posledního úspěšného uložení. */
  lastSavedAt?: number;
  pageSetup: PageSetup;
  /** HTML obsah záhlaví (Fáze 2). Prázdné = žádné záhlaví. */
  headerHtml?: string;
  /** HTML obsah zápatí (Fáze 2). Prázdné = žádné zápatí. */
  footerHtml?: string;
  /** Nastavení automatického čísla stránky. */
  pageNumber?: PageNumberSetup;
  /** Komentáře v dokumentu (Fáze 3). Vazba na text je přes mark s commentId. */
  comments?: DocComment[];
  /** Je zapnuté sledování změn (Track Changes)? Fáze 3. */
  trackChanges?: boolean;
  /** Režim zobrazení revizí (jako dropdown „All Markup" ve Wordu). */
  markupView?: MarkupView;
}

/**
 * Režim zobrazení sledovaných změn — odpovídá dropdownu ve Wordu.
 * Je to čistě vizuální vrstva; dokument se nemění.
 *   all      – Všechny revize: vložené i smazané se značkami
 *   simple   – Jednoduché revize: výsledný text + pruh na okraji
 *   none     – Bez revizí: jak bude dokument vypadat po přijetí
 *   original – Originál: jak vypadal před změnami
 */
export type MarkupView = 'all' | 'simple' | 'none' | 'original';

/** Stav uložení pro stavový řádek. */
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Přihlášený uživatel (z auth-worker.js /auth/me). */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  tier: string;
  picture?: string;
}

export const DEFAULT_PAGE_SETUP: PageSetup = {
  size: 'A4',
  orientation: 'portrait',
  margins: { ...MARGIN_PRESETS.normal },
};
